import React from "react";
import { motion } from "framer-motion";
import { 
  FaCheckCircle, 
  FaMicroscope, 
  FaSmile, 
  FaRupeeSign, 
  FaShieldAlt, 
  FaHandsHelping,
  FaArrowRight
} from "react-icons/fa";

export default function WhyChooseUs() {
  const reasons = [
    { 
      icon: <FaCheckCircle className="text-lg sm:text-xl" />, 
      title: "Experienced Dental Doctors", 
      desc: "Skilled and qualified dental professionals delivering personalized treatment." 
    },
    { 
      icon: <FaMicroscope className="text-lg sm:text-xl" />, 
      title: "Advanced Dental Technology", 
      desc: "Modern equipment and digital tools for accurate diagnosis and precise treatment." 
    },
    { 
      icon: <FaSmile className="text-lg sm:text-xl" />, 
      title: "Painless Treatment", 
      desc: "Gentle and comfortable procedures focused on patient comfort and care." 
    },
    { 
      icon: <FaRupeeSign className="text-lg sm:text-xl" />, 
      title: "Affordable Dental Care", 
      desc: "Quality dental treatments at transparent and reasonable pricing." 
    },
    { 
      icon: <FaShieldAlt className="text-lg sm:text-xl" />, 
      title: "Sterilized & Hygienic Clinic", 
      desc: "Strict sterilization protocols and hygienic environment for patient safety." 
    },
    { 
      icon: <FaHandsHelping className="text-lg sm:text-xl" />, 
      title: "Personalized Patient Care", 
      desc: "Individual attention and customized treatment plans for every patient." 
    },
  ];

  return (
    <section id="why-choose-us" className="py-16 sm:py-20 md:py-24 px-4 sm:px-6 bg-gradient-to-br from-[#F8FBFC] to-white relative overflow-hidden">
      
      <div className="absolute inset-0">
        <motion.div
          animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          className="absolute -top-40 -left-40 w-80 h-80 bg-teal-100/30 rounded-full blur-3xl"
        />
        <motion.div
          animate={{ scale: [1, 1.3, 1], opacity: [0.2, 0.4, 0.2] }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut", delay: 1 }}
          className="absolute -bottom-40 -right-40 w-96 h-96 bg-teal-50/40 rounded-full blur-3xl"
        />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto">
        
        <div className="text-center mb-12 sm:mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 bg-teal-50/80 backdrop-blur-sm px-4 py-2 rounded-full mb-4 border border-teal-100"
          >
            <span className="w-1.5 h-1.5 bg-teal-500 rounded-full animate-pulse"></span>
            <span className="text-teal-600 text-xs sm:text-sm font-semibold tracking-wide">WHY CHOOSE US</span>
            <span className="w-1.5 h-1.5 bg-teal-500 rounded-full animate-pulse"></span>
          </motion.div>
          
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            viewport={{ once: true }}
            className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#1E293B] mb-4 leading-tight"
          >
            Why Choose{" "}
            <span className="bg-gradient-to-r from-[#2CA6B1] to-[#4FD1C5] bg-clip-text text-transparent">
              IQRA Multispeciality Dental Clinic?
            </span>
          </motion.h2>
          
          <motion.div
            initial={{ opacity: 0, width: 0 }}
            whileInView={{ opacity: 1, width: "5rem" }}
            transition={{ duration: 0.5, delay: 0.2 }}
            viewport={{ once: true }}
            className="h-1 bg-gradient-to-r from-[#2CA6B1] to-[#4FD1C5] mx-auto rounded-full"
          />
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            viewport={{ once: true }}
            className="text-gray-500 text-sm sm:text-base max-w-2xl mx-auto mt-4"
          >
            Committed to providing exceptional dental care with modern technology, experienced professionals and patient-first approach.
          </motion.p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {reasons.map((reason, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 25 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: idx * 0.05 }}
              viewport={{ once: true }}
              className="group"
            >
              <div className="relative overflow-hidden flex items-start gap-4 p-5 sm:p-6 bg-white rounded-2xl shadow-md hover:shadow-xl hover:-translate-y-2 transition-all duration-300 border border-gray-100 cursor-pointer">
                
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent -skew-x-12 pointer-events-none"
                  initial={{ x: "-100%" }}
                  whileHover={{ x: "100%" }}
                  transition={{ duration: 0.5, ease: "easeInOut" }}
                />
                
                <div className="relative z-10 flex items-start gap-4">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-teal-50 flex items-center justify-center text-teal-500 group-hover:bg-teal-500 group-hover:text-white transition-all duration-300 flex-shrink-0">
                    {reason.icon}
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#1E293B] text-sm sm:text-base mb-1 group-hover:text-teal-600 transition-colors">
                      {reason.title}
                    </h3>
                    <p className="text-xs sm:text-sm text-gray-500 leading-relaxed">
                      {reason.desc}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          viewport={{ once: true }}
          className="mt-12 sm:mt-16 text-center"
        >
          <a
            href="#contact"
            className="inline-flex items-center gap-2 px-6 sm:px-8 py-2.5 sm:py-3 rounded-xl bg-gradient-to-r from-[#2CA6B1] to-[#4FD1C5] text-white font-semibold text-sm sm:text-base shadow-lg hover:scale-105 hover:shadow-xl transition-all duration-300"
          >
            Book Dental Appointment
            <FaArrowRight className="text-sm sm:text-base" />
          </a>
        </motion.div>
      </div>
    </section>
  );
}